import Alpine from 'alpinejs';
import api, { requireAuth } from './api.js';

function createInkoCreationProject() {
  return {
    projectId: '',
    loading: true,
    busy: false,
    error: null,
    tab: 'template',
    project: null,
    templates: [],
    canvas: null,
    assets: [],
    assetQuery: '',
    assetType: '',
    pickZone: null,
    pickType: '',
    job: null,
    pollTimer: null,

    init() {
      this.projectId = this.$el.dataset.projectId || '';
      if (!requireAuth() || !this.projectId) return;
      this.load();
    },

    destroy() {
      if (this.pollTimer) clearInterval(this.pollTimer);
    },

    async load() {
      this.loading = true;
      try {
        this.project = await api.showProject(this.projectId);
        const productKey = this.project.product_key;
        const theme = this.project.intent_json?.theme;
        this.templates = await api.listTemplates(productKey, theme);
        await this.loadCanvas();
      } catch (e) {
        this.error = e.response?.data?.message || 'Gagal memuat proyek.';
      } finally {
        this.loading = false;
      }
    },

    async loadCanvas() {
      try {
        this.canvas = await api.getCanvas(this.projectId);
      } catch {
        this.canvas = null;
      }
    },

    get aspectRatio() {
      const w = Number(this.canvas?.canvas?.width_cm || 3);
      const h = Number(this.canvas?.canvas?.height_cm || 1);
      return w / Math.max(h, 0.1);
    },

    async applyTemplate(key) {
      this.busy = true;
      try {
        this.project = await api.applyTemplate(this.projectId, key);
        await this.loadCanvas();
        this.tab = 'canvas';
        window.toast?.('Template diterapkan.', 'success');
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal menerapkan template.', 'error');
      } finally {
        this.busy = false;
      }
    },

    openAssetPicker(zoneKey, type) {
      this.pickZone = zoneKey;
      this.pickType = type || '';
      this.assetType = type || '';
      this.tab = 'assets';
      this.searchAssets();
    },

    async searchAssets() {
      try {
        this.assets = await api.searchAssets({
          q: this.assetQuery || undefined,
          type: this.assetType || undefined,
        });
      } catch {
        this.assets = [];
      }
    },

    async attachAsset(assetId) {
      if (!this.pickZone) return;
      this.busy = true;
      try {
        this.canvas = await api.attachAsset(this.projectId, this.pickZone, assetId);
        this.pickZone = null;
        this.tab = 'canvas';
        window.toast?.('Aset dilampirkan.', 'success');
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal melampirkan aset.', 'error');
      } finally {
        this.busy = false;
      }
    },

    async resolveAssets() {
      this.busy = true;
      try {
        this.project = await api.resolveAssets(this.projectId);
        window.toast?.('Aset di-resolve ulang.', 'success');
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Resolve gagal.', 'error');
      } finally {
        this.busy = false;
      }
    },

    async startGenerate() {
      this.busy = true;
      this.error = null;
      try {
        const payload = await api.generateBackground(this.projectId);
        this.job = payload;
        if (payload.status === 'completed') {
          await this.loadCanvas();
          this.tab = 'canvas';
        } else {
          this.startPoll();
        }
      } catch (e) {
        this.error = e.response?.data?.message || 'Generate gagal.';
      } finally {
        this.busy = false;
      }
    },

    startPoll() {
      if (this.pollTimer) clearInterval(this.pollTimer);
      this.pollTimer = setInterval(async () => {
        try {
          const status = await api.generationStatus(this.projectId);
          if (status.latest_job) {
            this.job = status.latest_job;
            if (status.latest_job.status === 'completed') {
              clearInterval(this.pollTimer);
              await this.loadCanvas();
              this.tab = 'canvas';
              window.toast?.('Background selesai di-generate.', 'success');
            } else if (status.latest_job.status === 'failed') {
              clearInterval(this.pollTimer);
              this.error = status.latest_job.error_message || 'Generate gagal.';
            }
          }
        } catch { /* ignore poll errors */ }
      }, 2500);
    },

    layerStyle(layer) {
      const t = layer.transform || {};
      return {
        left: `${t.x_pct ?? 0}%`,
        top: `${t.y_pct ?? 0}%`,
        width: `${t.w_pct ?? 10}%`,
        height: `${t.h_pct ?? 10}%`,
        opacity: layer.visible === false ? 0.3 : Number(layer.style?.opacity ?? 1),
      };
    },

    layerClass(layer) {
      let c = 'inko-layer';
      if (layer.type === 'background') c += ' is-bg';
      return c;
    },
  };
}

Alpine.data('inkoCreationProject', createInkoCreationProject);
